import React, { Fragment } from 'react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { View } from 'react-native';
import { styled, useResponsiveValue } from 'dripsy';
import { useModal } from 'react-native-modalfy';
import { useNavigation } from '@react-navigation/native';

import {
  BaseText,
  Separator,
  LockCaseSvg,
  Button,
  SubscribeSuggestionModalTypeEnum,
} from '@components';
import { useTracksModel } from '@models/Tracks/TracksModel';
import { IS_WEB } from '@utils';
import { modals } from '@modals';
import { screens, TabNavigatorNavigationPropType } from '@navigation';
import type { Credits } from '@models/Tracks/CreditsModel';

const Container = styled(View)({
  bg: '$primary80',
  alignItems: [null, 'center'],
  flex: 1,
  flexDirection: ['column', 'column', 'column', 'row'],
  borderRadius: 4,
  px: ['$6', '$12'],
  py: '$5',
});

const PurchaseButton = styled(Button)({
  width: 213,
});

const CreditsInfoContainer = styled(View)({
  height: '100%',
  py: '$9',
  flex: 1,
});

const CreditsTitle = styled(BaseText)({
  color: '$text400',
  variant: 'text.medium',
  fontWeight: 'semi',
});

const CreditsNumber = styled(BaseText)({
  color: '$heading800',
  variant: 'text.xxxxbig',
  fontWeight: 'semi',
});

const BalanceContainer = styled(View)({
  flexDirection: 'row',
  alignItems: 'flex-end',
  justifyContent: 'space-between',
});

const MobileCreditsNumber = styled(BaseText)({
  color: '$heading800',
  variant: 'text.big',
  fontWeight: 'semi',
});

const MobileCreditsTitle = styled(BaseText)({
  color: '$text400',
  variant: 'text.small',
  fontWeight: 'semi',
});

const CreditsDescription = styled(BaseText)({
  color: '$heading800',
  variant: 'text.medium',
  fontWeight: 'semi',
  flexShrink: 1,
});

const LockCaseSvgStyled = styled(LockCaseSvg)({
  width: [132, 236],
  height: [99, 176],
});

type NavigationPropType =
  TabNavigatorNavigationPropType<'TrackFeedback'>;

export const CreditsStatusView = observer(() => {
  const { t } = useTranslation();
  const track = useTracksModel();
  const nav = useNavigation<NavigationPropType>();
  const modal = useModal();
  const svgHeight = useResponsiveValue([99, 176]);
  const svgWidth = useResponsiveValue([132, 236]);

  function navigateToSubscription() {
    nav.navigate(screens.ProfileNavigator, {
      screen: screens.Subscription,
    });
  }

  function onNavigateToPayment(params: Credits) {
    nav.navigate(screens.FeedbackNavigator, {
      screen: screens.CreditsPayment,
      params: params,
    });
  }

  function openPurchaseCreditsModal() {
    if (!track.feedback.isAvailableForCurrentPlan) {
      modal.openModal(modals.SubscribeSuggestionModal, {
        onPress: () => {
          navigateToSubscription();
          modal.closeModal();
        },
        type: SubscribeSuggestionModalTypeEnum.TrackFeedback,
      });
    } else {
      modal.openModal(modals.PurchaseCreditsModal, {
        onNavigateToPayment,
      });
    }
  }

  const hasCredits = !!track.feedback.credits?.count;

  return IS_WEB ? (
    <Fragment>
      <Container>
        <LockCaseSvgStyled width={svgWidth} height={svgHeight} />
        <Separator vertical size="$10" />
        <CreditsInfoContainer>
          <CreditsTitle>
            {t('trackFeedback.balanceTitle')}
          </CreditsTitle>
          <CreditsNumber>
            {`${t('trackFeedback.balanceNumber', {
              count: track.feedback.credits?.count,
            })} 💰`}
          </CreditsNumber>
          <Separator size="$4" />
          {hasCredits ? (
            <Fragment>
              <CreditsDescription>
                {t('trackFeedback.balanceHasCreditsDescription', {
                  date: track.feedback.credits
                    ?.formattedExpirationTime,
                })}
              </CreditsDescription>
              <Separator size="$4" />
              <CreditsDescription>
                {t('trackFeedback.balanceHasCreditsSubDescription')}
              </CreditsDescription>
            </Fragment>
          ) : (
            <Fragment>
              <CreditsDescription>
                {t('trackFeedback.balanceDescription')}
              </CreditsDescription>
              <Separator size="$4" />
              <PurchaseButton onPress={openPurchaseCreditsModal}>
                {t('trackFeedback.balancePurchaseButtonTitle')}
              </PurchaseButton>
            </Fragment>
          )}
        </CreditsInfoContainer>
      </Container>
      <Separator vertical size="$7" />
    </Fragment>
  ) : (
    <Container>
      <BalanceContainer>
        <View>
          <MobileCreditsTitle>
            {t('trackFeedback.balanceTitle')}
          </MobileCreditsTitle>
          <MobileCreditsNumber>
            {`${t('trackFeedback.balanceNumber', {
              count: track.feedback.credits?.count,
            })} 💰`}
          </MobileCreditsNumber>
        </View>
        <LockCaseSvgStyled width={svgWidth} height={svgHeight} />
      </BalanceContainer>
      <Separator size="$4" />
      {hasCredits ? (
        <Fragment>
          <CreditsDescription>
            {t('trackFeedback.balanceHasCreditsDescription', {
              date: track.feedback.credits?.formattedExpirationTime,
            })}
          </CreditsDescription>
          <Separator size="$4" />
          <CreditsDescription>
            {t('trackFeedback.balanceHasCreditsSubDescription')}
          </CreditsDescription>
        </Fragment>
      ) : (
        <Fragment>
          <CreditsDescription>
            {t('trackFeedback.balanceDescription')}
          </CreditsDescription>
          <Separator size="$6" />
          <CreditsDescription>
            {t('trackFeedback.balanceDescriptionBottomMobile')}
          </CreditsDescription>
        </Fragment>
      )}
    </Container>
  );
});
